import type {
  CSSObject,
  TextInputProps as MantineTextInputProps,
  PasswordInputProps,
  AutocompleteProps as MantineAutocompleteProps,
  SelectProps as MantineSelectProps,
} from '@mantine/core';
import {
  PasswordInput as MantinePasswordInput,
  TextInput as MantineTextInput,
  Autocomplete as MantineAutocomplete,
  Select as MantineSelect,
  createStyles,
} from '@mantine/core';

import { textStyles } from './Text';
import IconLock from './icons/IconLock';

export type InputBase = {
  label?: string | JSX.Element;
  value?: string;
  error?: string;
  description?: string;
  hasBorder?: boolean;
  variant?: 'filled' | 'unstyled';
  locked?: boolean;
};

type TextInputProps = InputBase & MantineTextInputProps;
type AutocompleteProps = InputBase & MantineAutocompleteProps;
type SelectProps = InputBase & MantineSelectProps;

type PhoneInputProps = TextInputProps & {
  prefix: string | JSX.Element;
};

type StyleArgs = Pick<TextInputProps, 'label' | 'error' | 'value' | 'hasBorder'> & {
  isTextarea?: boolean;
  alwaysSmallLabel?: boolean;
};

export const useStyles = createStyles(
  (
    theme,
    {
      label,
      error,
      value,
      isTextarea,
      hasBorder,
      alwaysSmallLabel,
      variant,
    }: StyleArgs & { variant?: 'filled' | 'unstyled' }
  ) => {
    const input = {
      ...(isTextarea ? textStyles['body-large'] : textStyles['title-medium']),
      padding: /* eslint-disable-line local-rules/use-theme-tokens */ '5px 16px',
      minHeight: '48px',

      ...(label && {
        ...(value?.length > 0 && { paddingTop: /* eslint-disable-line local-rules/use-theme-tokens */ '23px' }),
        '&:focus, &:autofill, &:-webkit-autofill': {
          paddingTop: /* eslint-disable-line local-rules/use-theme-tokens */ '23px',
        },
      }),

      '&, &.mantine-TextInput-invalid': {
        color: theme.colors.gray[9],
        borderColor: 'transparent !important',
      },

      // Override Mantine's default input focus styles
      '&.mantine-TextInput-input:focus': {
        outline: 'none',
        borderColor: 'transparent',
      },

      '&.mantine-TextInput-input:focus-within': {
        outline: 'none',
        borderColor: 'transparent',
      },
      ...(variant === 'filled' && {
        '&, &.mantine-TextInput-invalid': {
          borderColor: 'transparent',
        },
      }),
    } as CSSObject;

    return {
      root: {
        position: 'relative',
        lineHeight: 1,

        '&:focus-within': {
          label: { ...textStyles['body-small'], top: 5 },
        },

        ...(variant === 'filled' && {
          '& input:focus': {
            outline: 'none',
            border: 'none',
          },
        }),
      },
      wrapper: {
        marginBottom: 0,
        minHeight: '48px',
        background: theme.white,
        borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ '8px',
        overflow: 'hidden',

        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'transparent',

        ...(variant === 'filled' && {
          background: theme.colors.gray[1],
          borderColor: 'transparent',
          '&:focus-within': {
            borderColor: theme.colors.gray[6],
            background: theme.white,
          },
        }),

        ...(variant !== 'filled' && {
          borderColor: hasBorder ? theme.colors.gray[4] : 'transparent',
          '&:focus-within': {
            borderColor: theme.colors.gray[6],
          },
        }),

        '&:focus, &:focus-within': {
          outline: 'none',
          borderWidth: '1px',
        },

        ...(error?.length > 0 && {
          borderColor: theme.colors.orange[3],
        }),
        '.mantine-Autocomplete-wrapper &, .mantine-Select-wrapper &': {
          border: 'none',
        },
      },
      input,
      innerInput: input,
      label: {
        position: 'absolute',
        top: 5,
        left: 16,
        zIndex: 1,
        color: theme.colors.gray[6],
        ...(value?.length > 0 || alwaysSmallLabel
          ? textStyles['body-small']
          : {
              ...textStyles['title-medium'],
              top: 14,
            }),
        transition: '0.2s font-size, 0.2s line-height, 0.2s font-weight, 0.2s top',
      },
      description: textStyles['body-medium'],
      error: {
        margin: /* eslint-disable-line local-rules/use-theme-tokens */ '8px 0 0',
        ...textStyles['body-medium'],
        color: theme.colors.orange[3],
      },
      icon: {
        marginTop: /* eslint-disable-line local-rules/use-theme-tokens */ label ? '16px' : 0,
        fontSize: '14px',
        color: '#1A1C1E',
      },
    };
  }
);

export function TextInput({
  hasBorder,
  variant = 'unstyled',
  locked = false,
  ...props
}: TextInputProps) {
  const { classes, cx, theme } = useStyles({ ...props, hasBorder });

  return (
    <MantineTextInput
      {...props}
      disabled={locked || props.disabled}
      rightSection={
        locked ? (
          <IconLock size={24} color={props.error ? theme.colors.orange[3] : theme.colors.gray[4]} />
        ) : (
          props.rightSection
        )
      }
      classNames={{
        root: cx(classes.root, props.classNames?.root),
        wrapper: cx(classes.wrapper, props.classNames?.wrapper),
        input: cx(classes.input, props.classNames?.input),
        label: cx(classes.label, props.classNames?.label),
        description: cx(classes.description, props.classNames?.description),
        error: cx(classes.error, props.classNames?.error),
        icon: cx(classes.icon, props.classNames?.icon),
      }}
      variant={variant}
    />
  );
}

export function PasswordInput({ variant = 'unstyled', ...props }: InputBase & PasswordInputProps) {
  const { classes } = useStyles(props);

  return <MantinePasswordInput classNames={classes} variant={variant} {...props} />;
}

// @todo move autocomplete to its own design-system component & create story
export function Autocomplete({ variant = 'unstyled', ...props }: AutocompleteProps) {
  const { classes, cx } = useStyles(props);

  return (
    <MantineAutocomplete
      {...props}
      classNames={{
        root: cx(classes.root, props.classNames?.root),
        wrapper: cx(classes.wrapper, props.classNames?.wrapper),
        input: cx(classes.input, props.classNames?.input),
        label: cx(classes.label, props.classNames?.label),
        description: cx(classes.description, props.classNames?.description),
        error: cx(classes.error, props.classNames?.error),
      }}
      variant={variant}
    />
  );
}

export function PhoneInput({ prefix, variant = 'unstyled', ...props }: PhoneInputProps) {
  const { classes, cx } = useStyles({ ...props, alwaysSmallLabel: true });

  return (
    <MantineTextInput
      {...props}
      classNames={{
        root: cx(classes.root, props.classNames?.root),
        wrapper: cx(classes.wrapper, props.classNames?.wrapper),
        input: cx(classes.input, props.classNames?.input),
        label: cx(
          classes.label,
          textStyles['body-small'] // Force the small label style here
        ),
        description: cx(classes.description, props.classNames?.description),
        error: cx(classes.error, props.classNames?.error),
        icon: cx(classes.icon),
      }}
      icon={prefix}
      variant={variant}
    />
  );
}

export function Select({ variant = 'unstyled', ...props }: SelectProps) {
  const { classes, cx } = useStyles(props);

  return (
    <MantineSelect
      {...props}
      classNames={{
        root: cx(classes.root, props.classNames?.root),
        wrapper: cx(classes.wrapper, props.classNames?.wrapper),
        input: cx(classes.input, props.classNames?.input),
        label: cx(classes.label, props.classNames?.label),
        description: cx(classes.description, props.classNames?.description),
        error: cx(classes.error, props.classNames?.error),
      }}
      variant={variant}
    />
  );
}
